import React, { useState, createContext, useContext, ReactNode, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { useCookies } from 'react-cookie';
import { getUserByToken } from '../api/api';
import { useAuth } from '../app/modules/auth/core/Auth';
//import { AuthModel } from '../app/modules/auth/core/_models';

interface IUserRoleContext {
  roles: string[];
  setRoles: (roles: string[]) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (nome: string, cognome: string, email: string, password: string) => Promise<void>;
  isAuthenticating: boolean;
}

const UserRoleContext = createContext<IUserRoleContext | null>(null);

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(['api_token']);
  const [roles, setRoles] = useState<string[]>([]);
  const { saveAuth, setCurrentUser } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Quando l'app si avvia, controlla se c'è un token salvato e, in tal caso, ottieni il profilo
  useEffect(() => {
    const checkAuth = async () => {
      if (!cookies.api_token) {
        return;
      }
      try {
        // Ora getUserByToken chiama /api/auth/profile e restituisce il profilo completo, inclusi i dati delle associazioni (user_organizzazione) che contengono i ruoli.
        const user = await getUserByToken(cookies.api_token);
        if (user && user.user_organizzazione && Array.isArray(user.user_organizzazione)) {
          const extractedRoles = user.user_organizzazione.map(
            (assoc: any) => assoc.ruolo_organizzazione?.Nome
          );
          setRoles(extractedRoles);
        }
        setCurrentUser(user);
      } catch (error) {
        console.error('Errore durante il controllo dell\'autenticazione', error);
      }
    };

    checkAuth();
  }, [cookies.api_token, setCurrentUser]);

  const login = async (email: string, password: string) => {
    setIsAuthenticating(true);
    try {
      // La chiamata all'endpoint di login restituisce { success: true, token: '...' }
      const response = await axios.post('/api/auth/login', { email, password });
      const { data } = response;
      if (data.success) {
        setCookie('api_token', data.token, { secure: false, httpOnly: false });
        saveAuth({ api_token: data.token });

        // Otteniamo il profilo utente completo dall'endpoint /api/auth/profile
        const user = await getUserByToken(data.token);
        // Se il profilo include le associazioni, estraiamo i ruoli
        if (user && user.user_organizzazione && Array.isArray(user.user_organizzazione)) {
          const extractedRoles = user.user_organizzazione.map(
            (assoc: any) => assoc.ruolo_organizzazione?.Nome
          );
          setRoles(extractedRoles);
        }
        setCurrentUser(user);
      } else {
        throw new Error('Credenziali login non valide');
      }
    } catch (error) {
      console.error('Errore durante il login', error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const register = async (nome: string, cognome: string, email: string, password: string) => {
    setIsAuthenticating(true);
    try {
      const response = await axios.post('/api/auth/register', { nome, cognome, email, password });
      const { data } = response;
      console.log(data.success);
      if (data.success) {
        setCookie('api_token', data.token, { secure: false, httpOnly: false });
        // Otteniamo il profilo utente completo
        const user = await getUserByToken(data.token);
        if (user && user.user_organizzazione && Array.isArray(user.user_organizzazione)) {
          const extractedRoles = user.user_organizzazione.map(
            (assoc: any) => assoc.ruolo_organizzazione?.Nome
          );
          setRoles(extractedRoles);
        }
        setCurrentUser(user);
      } else {
        throw new Error('Errore durante la registrazione');
      }
    } catch (error) {
      console.error('Errore durante la registrazione', error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout', { token: cookies.api_token });
    } catch (error) {
      console.error('Errore durante il logout', error);
    }
    removeCookie('api_token', { path: '/' });
    setRoles([]);
  };

  const value = { roles, setRoles, login, logout, register, isAuthenticating };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};

export function useRoles() {
  const context = useContext(UserRoleContext);
  if (context === null) {
    throw new Error('useRoles must be used within a UserRoleProvider');
  }
  return context;
}